import type { Paper, PackContent, PackDef, PackRule } from './types';

/**
 * Mode tie-breaker preference. When a single (subject, paperYear) pair has
 * multiple variants, only one is included in a pack — Online wins over
 * Interactive PDF wins over PDF.
 */
const MODE_PRIORITY: Record<string, number> = {
  Online: 0,
  'Interactive PDF': 1,
  PDF: 2,
};

function modeRank(mode: string): number {
  const rank = MODE_PRIORITY[mode];
  return rank === undefined ? 99 : rank;
}

/**
 * Group papers by (subject, paperYear), keeping only the highest-priority mode
 * for each pair.
 */
function pickByModeTieBreaker(papers: Paper[]): Paper[] {
  const bySubjectAndYear = new Map<string, Paper>();
  papers.forEach((paper) => {
    const key = `${paper.subject}::${paper.paperYear}`;
    const existing = bySubjectAndYear.get(key);
    if (!existing || modeRank(paper.mode) < modeRank(existing.mode)) {
      bySubjectAndYear.set(key, paper);
    }
  });
  return Array.from(bySubjectAndYear.values());
}

/** Papers for one subject, mode-deduped, sorted by paperYear descending. */
function subjectPapersSorted(papers: Paper[], subject: string): Paper[] {
  return pickByModeTieBreaker(papers.filter((p) => p.subject === subject)).sort(
    (a, b) => parseInt(b.paperYear, 10) - parseInt(a.paperYear, 10),
  );
}

/**
 * Take the top N most-recent papers per selected subject. If a subject has
 * fewer than N papers, take what's available.
 */
function topNPerSubject(papers: Paper[], subjects: string[], n: number): Paper[] {
  const result: Paper[] = [];
  for (const subject of subjects) {
    result.push(...subjectPapersSorted(papers, subject).slice(0, n));
  }
  return result;
}

/**
 * Top up a pack to `target` papers by pulling extras from the selected
 * subjects in priority order. Checks the selected year first, then walks
 * down through lower year levels. Used by Foundation and Essential to avoid
 * showing under-filled packs when a subject has fewer papers than expected.
 */
function fillToTarget(
  primary: Paper[],
  target: number,
  allPapers: Paper[],
  subjects: string[],
  selectedYear: string,
  yearLevelOrder: string[],
): Paper[] {
  if (primary.length >= target) return primary;
  const result = [...primary];
  const used = new Set(result.map((p) => p.variantId));
  const selectedIdx = yearLevelOrder.indexOf(selectedYear);

  for (const subject of subjects) {
    if (result.length >= target) break;
    for (let i = selectedIdx; i >= 0 && result.length < target; i -= 1) {
      const code = yearLevelOrder[i];
      const candidates = allPapers.filter(
        (p) => p.subject === subject && p.difficulties.includes(code) && !used.has(p.variantId),
      );
      const sorted = pickByModeTieBreaker(candidates).sort(
        (a, b) => parseInt(b.paperYear, 10) - parseInt(a.paperYear, 10),
      );
      for (const p of sorted) {
        if (result.length >= target) break;
        result.push(p);
        used.add(p.variantId);
      }
    }
  }
  return result;
}

/**
 * Foundation pack.
 *   - 1 subject  → top 2 most-recent papers
 *   - 2+ subjects → top 1 most-recent paper per subject
 *
 * 2-subject fallback: if the primary "1 per subject" yields fewer than 2,
 * top up from subjects that have papers so the pack never displays as a
 * single paper.
 */
export const foundationRule: PackRule = ({ papers, allPapers, subjects, selectedYear, yearLevelOrder }) => {
  if (subjects.length === 1) return topNPerSubject(papers, subjects, 2);
  const primary = topNPerSubject(papers, subjects, 1);
  if (subjects.length !== 2) return primary;
  return fillToTarget(primary, 2, allPapers, subjects, selectedYear, yearLevelOrder);
};

/**
 * Essential pack.
 *   - 1 subject  → top 4 most-recent papers
 *   - 2+ subjects → top 2 most-recent papers per subject; if a subject is
 *     short, top up from the others so the pack hits `subjects.length × 2`.
 */
export const essentialRule: PackRule = ({ papers, allPapers, subjects, selectedYear, yearLevelOrder }) => {
  if (subjects.length === 1) return topNPerSubject(papers, subjects, 4);
  const primary = topNPerSubject(papers, subjects, 2);
  const target = subjects.length * 2;
  return fillToTarget(primary, target, allPapers, subjects, selectedYear, yearLevelOrder);
};

/**
 * Complete pack: every available paper for the selected subjects, mode-deduped
 * and sorted by paperYear descending within each subject.
 */
export const completeRule: PackRule = ({ papers, subjects }) => {
  const result: Paper[] = [];
  for (const subject of subjects) {
    result.push(...subjectPapersSorted(papers, subject));
  }
  return result;
};

export const PACKS: PackDef[] = [
  { id: 'foundation', contentKey: 'foundation_practice_pack', rule: foundationRule },
  { id: 'essential', contentKey: 'essential_practice_pack', rule: essentialRule },
  { id: 'complete', contentKey: 'complete_practice_pack', rule: completeRule },
];

/**
 * Fallback display copy used when the merchant has not authored the matching
 * `past_paper_pack_content` metaobject entry. Keyed by `contentKey`.
 */
export const PACK_CONTENT_DEFAULTS: Record<string, PackContent> = {
  foundation_practice_pack: {
    displayName: 'Foundation practice',
    description:
      'An introduction to ICAS preparation that helps students start building confidence and familiarity with unique ICAS question styles and difficulty levels.',
    tagline: 'Build familiarity',
    badgeText: '',
    popular: false,
  },
  essential_practice_pack: {
    displayName: 'Essential practice',
    description:
      'For students who benefit from broader exposure to ICAS question styles, helping them feel capable, prepared and ready for competition day.',
    tagline: 'Prepare thoroughly',
    badgeText: 'Most popular',
    popular: true,
  },
  complete_practice_pack: {
    displayName: 'Complete practice',
    description:
      'The most extensive preparation, helping students maximise familiarity with ICAS question styles and feel fully prepared to achieve their best.',
    tagline: 'Maximum readiness',
    badgeText: '',
    popular: false,
  },
};

/**
 * Volume tier discount table. Discount percent for a pack is derived from the
 * resolved paper count, not stored on the pack definition.
 */
export function volumeDiscountPercent(paperCount: number): number {
  if (paperCount >= 11) return 40;
  if (paperCount >= 8) return 30;
  if (paperCount >= 5) return 25;
  if (paperCount >= 4) return 20;
  return 0;
}
